import { useCallback, useEffect, useRef, useState } from 'react';
import { printInOwnWindow } from '../utils/printWindow';
import { Card, CardHeader, CardTitle, CardContent, Button, Input } from './ui';
import ContactPicker from './ContactPicker';
import { api } from '../lib/api';
import type { LoiGenerateInput, LoiFilingPlan, LoiFilingRequest, LoiOmRead, LoiWouldCreate } from '../types';
import { fileToBase64 } from '../lib/fileToBase64';
import { readLoiInput } from './loi/readLoiInput';
import { enterNeverFiles } from './loi/enterNeverFiles';
import { correctionNotes, type RecordFacts } from './loi/recordFacts';
import { useLoiOverrides } from './loi/useLoiOverrides';
import LoiPreviewPanel from './loi/LoiPreviewPanel';
import { KeepAsHouseStyle } from './loi/LoiHouseStyle';
import LoiWhatIfPrice from './loi/LoiWhatIfPrice';
import { usePageAction, usePageActionFallback } from '../contexts/PageActionContext';
import { BESIDE_DOCKED_RAIL, useAssistantRail } from '../contexts/AssistantRailContext';
import type { HouseStyleRule } from '../types/loiOverride';

/**
 * The LOI generator, reachable from wherever the work happens.
 *
 * ONE component, three callers - the deal page, the property database and the analyzer
 * property page - because the letter is the same letter and three hand-kept copies of a legal
 * document's form is the drift this repo keeps paying for. What differs is only what the caller
 * can PREFILL and whether there is a deal to file against.
 *
 * With `dealId` the generated letter is FILED into that deal (it shows in the Deal Room
 * documents list immediately). Without one it is generated and downloaded - a property with no
 * deal has nowhere a filing would ever be seen.
 */

/** What `listingBroker.ts` kept off the OM. Displayed, never printed by the letter. */
export interface LoiListingBroker {
  brokerage: string | null;
  agentName: string | null;
  agentPhone: string | null;
  agentEmail: string | null;
  agentLicense: string | null;
  source: string;
}

export interface LoiPrefill {
  /** Suggested, always editable. */
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  buildingSf?: number | null;
  county?: string | null;
  /**
   * ⭐ THE CORRECTION NOTE (spec 3.9, the 113th): the building facts AS THE RECORD HOLDS THEM, kept apart from what the
   *   chat, a counter read or his typing puts in the letter's boxes - so the dialog can say, in one line to him, where
   *   the letter differs. Set only by a record page (`recordFactsOf`); a letter with no record behind it has none.
   */
  recordFacts?: RecordFacts;
  purchasePrice?: number | null;
  /**
   * ⭐ THE DEAL'S OWN PRICE (`offer_price`, else `deal_value`), for the what-if's note - set ONLY by the deal page, and
   *   BEFORE the chat's terms. `purchasePrice` above can be a price he stated for THIS letter (the page lays the chat's
   *   terms over the record's on purpose), so it is not the deal's. ⛔ The chat has no field of this name; the letter
   *   never prints it.
   */
  dealPrice?: number | null;
  emdAmount?: number | null;
  /**
   * DOC3 - read off the OM filed on this deal's property.
   *
   * ⛔⛔ SUGGESTIONS, AND LABELLED AS SUCH. A wrong broker on an LOI reaches a counterparty on a
   * document carrying his DRE number, so the operator's eye is the gate - and a suggestion that
   * looks like a confirmed fact removes the gate without removing the risk.
   * ⚠️ Three fields because that is what the letter prints, read out of `loiBuilderService`:
   * `c/o {careOf}` and `Attn: {attn}` in the recipient block, and "Seller is represented by
   * {sellersBrokerage}" in the Brokerage section. ⛔ The LICENCE is printed nowhere and prefills
   * nothing - in an OM it is usually the FIRM's, off a copyright footer.
   */
  sellersBrokerage?: string | null;
  listingAttn?: string | null;
  listingBroker?: LoiListingBroker | null;
  brokerSource?: string | null;
  expiration?: string | null;
  sellerName?: string | null;
  counterToSellerCounterNo?: number | null;
  /**
   * The 97th, both of them reachable from the AI rail as well as the form.
   * ⭐ `salutationName` greets the listing agent while the header names the owner of record -
   *   his Olive Garden convention. ⭐ `buyerProfile` picks between his two Buyer Profile
   *   sentences; omitted, the builder uses the all-cash 1031 one his recent letters use.
   */
  salutationName?: string | null;
  buyerProfile?: 'all_cash_1031' | 'financed_1031' | 'none';
  balanceKind?: 'all_cash' | 'cash_balance' | 'cash' | 'new_loan' | 'seller_financing';
  downPayment?: number | null;
  is1031?: boolean;
  downLegCloseDate?: string | null;
  /** Site size, so a recordless letter can state it. The form has always had the input. */
  acres?: number | null;
  /**
   * 🚨🚨 THE RE-LINE DESCRIPTION HE STATED (the 100th). The 97th's tool carried it through every
   *    link except the last two - this type never declared it and the input had no `defaultValue`,
   *    so a REQUIRED box opened empty right after he said what it should read.
   * ⛔ Only ever from his own words, or an OM he attached. Never derived from a column: that rule
   *   (`dealLoiPrefill`) is about a machine characterising his offer, and it still holds.
   */
  description?: string | null;
  /**
   * 🚨🚨 THE 101ST - WHAT HE SAYS ABOUT THE PARTIES, AND IT NEVER LANDED. Driven live with his real
   *    Applebee's terms, the buyer names, the c/o, the brokerage and "signing as an entity" were all
   *    stated in chat and none reached the form: no tool parameter, and no `defaultValue` here either.
   *    The c/o ended up inside the SELLER'S NAME, which prints on the seller's signature line.
   * ⭐ `careOf` is its own field now; unset, the c/o box still falls back to the brokerage, as the OM
   *   read and the deal prefill have always filled it.
   */
  buyerNames?: string[] | null;
  signatureHeading?: string | null;
  careOf?: string | null;
  sellerIsEntity?: boolean;
  /** The lines under the c/o in his header - the agent's email and phone, or a mailing address. */
  recipientLines?: string[] | null;
  /**
   * ⭐ The 101st - his September RE line: the tenant leads it, the lot SF and a location end it.
   * ⛔ PROPERTY IDENTITY like the address, so `AssistantPanel` strips them when a record is on screen.
   */
  reLineTenant?: string | null;
  lotSf?: number | null;
  reLineLocation?: string | null;
  /** The 101st - his wording era, his due-diligence list, and a 1031 not yet in escrow. */
  wording?: 'september_2026' | 'june_2026';
  ddList?: 'standard' | 'single_tenant_restaurant' | 'vacant' | 'restaurant_pad';
  downLegNotInEscrow?: boolean;
  /**
   * ⭐ LOI-S5 (the 127th) - his revised Olive Garden letters, each choice OFF unless he states it. The three dates are
   *   calendar days (YYYY-MM-DD) because the dialog's boxes are date boxes; the letter prints them in his long form.
   */
  revisesLetterDated?: string;
  revisesRevisedLetter?: boolean;
  downLegExtensionDays?: number;
  downLegExtensionFrom?: string;
  downLegProtection?: boolean;
  ddFromDeliverables?: boolean;
  coeDaysAfterDd?: number;
  coeOutsideDate?: string;
  /**
   * ⭐ The PDFs he attached in the ASSISTANT conversation, offered for the OM read (the 100th).
   * ⛔ Set by `AssistantPanel` from his own messages - never by the model, which cannot name one.
   */
  omAttachments?: { storagePath: string; fileName: string }[] | null;
  /**
   * ⛔⛔ WHAT THE DOCUMENT READ COULD NOT SETTLE, AND WHY. Without this a withheld field reads as a
   *    broken feature rather than a protected one - which is how a safety rule gets switched off.
   */
  counterNotices?: LoiCounterNotices | null;
  /**
   * ⭐ THE LOI OVERRIDE - his asks about SECTIONS, from the chat (`open_loi_dialog.section_instructions`), in his words.
   *   Each is drafted against the letter on screen and waits for his Accept. ⛔ Never a clause the model wrote.
   */
  sectionInstructions?: string[] | null;
}

export interface LoiCounterNotices {
  filename: string;
  /**
   * 🚨🚨🚨 Measured on his three LIVE seller counters: all three are scans of APEX'S OWN outbound
   *    LOI with the seller's terms in pen. The printed text is our offer, struck through - so on
   *    such a document a text-layer figure is the number the seller CROSSED OUT.
   */
  printedLayerIsOurOwnLetter: boolean;
  possibleHandwriting: boolean;
  gaps: string[];
  held: { field: string; reason: string }[];
  /**
   * ⭐⭐ THE PRINT A HANDWRITTEN REVISION BEAT. Measured on his live counters 2026-09-17: the field
   *    fills from the ink ($4,550,000) and this names the printed figure it replaced ($3,900,000),
   *    so nothing is dropped in silence and he can check the pair against the page.
   */
  superseded: { field: string; reason: string; values: string }[];
  /** Handwriting the reader saw and a guard refused - "July 7" with no year - so he can fill it himself. */
  handwrittenButUnverified: { label: string; quote: string; page: number }[];
  evidence: { field: string; quote?: string; page?: number; readFrom?: string; source: string; basis?: string; handwritten?: boolean }[];
}

interface GeneratedLoi {
  loi_text: string;
  loi_html: string;
  docx_base64: string;
  filename: string;
  filed: boolean;
  /** Set when a recordless letter was filed on a property (the 100th). */
  filing?: {
    filed_on: string; target_id: string; created_property: boolean;
    already_filed: boolean; changed_from_plan: string | null;
  } | null;
  /** Why a filing he asked for did not happen. ⛔ The letter itself still came back. */
  filing_refused?: string | null;
  /** D1: `false` - filed, but the list of his changes could not be recorded; `null` - nothing to record. */
  changes_recorded?: boolean | null;
}

/** The inputs the filing lookup reads. A change to any of them re-opens the question of WHERE. */
const FILING_INPUTS: readonly string[] = ['address', 'city', 'state', 'zip', 'building_sf', 'acres'];

/**
 * ⛔⛔ THE OM READ'S COMPLETE MAP ON THIS SIDE TOO. The server already refuses price, EMD,
 *    expiration, seller entity and cap rate - but a client that copied whatever keys came back
 *    would put a future server bug straight into his letter. So only these fill anything.
 */
const OM_TO_INPUTS: ReadonlyArray<[keyof LoiOmRead['prefill'], string[], string]> = [
  ['address', ['address'], 'Address'],
  ['city', ['city'], 'City'],
  ['state', ['state'], 'State'],
  ['zip', ['zip'], 'ZIP'],
  ['buildingSf', ['building_sf'], 'Building SF'],
  ['acres', ['acres'], 'Acres'],
  ['description', ['description'], 'RE-line description'],
  ['listingAttn', ['attn'], 'Attn'],
  // Both boxes the deal prefill fills from the same firm today - c/o and the Brokerage section.
  ['sellersBrokerage', ['sellers_brokerage', 'care_of'], "Seller's brokerage"],
];

/**
 * ⭐ AN OM'S TYPOGRAPHY IS NOT HIS LETTER'S. Found by looking, 2026-09-22: the Applebee's OM prints
 *   "ROBERT NARCHI" and "INDIO, CA", and filled verbatim the letter would read "Attn: ROBERT NARCHI".
 * ⛔ Only a person's name or a city, and only a value with NO lowercase letter at all - a firm keeps its
 *   capitals (CBRE, NAI Capital) and so does an address ("CA-111"). The evidence line still shows the
 *   document's words exactly, and the box stays editable.
 */
const RECASE_WHEN_SHOUTED: ReadonlySet<string> = new Set(['city', 'listingAttn']);
export const unshout = (s: string): string =>
  /[a-z]/.test(s) || !/[A-Z]/.test(s)
    ? s
    : s.toLowerCase().replace(/(^|[\s'-])([a-z])/g, (_m: string, p: string, c: string) => p + c.toUpperCase());

/** The filing facts as they stand in the form RIGHT NOW. */
const factsOf = (form: HTMLFormElement | null): LoiWouldCreate => {
  const fd = form ? new FormData(form) : null;
  const s = (n: string) => (((fd?.get(n) as string | null) ?? '')).trim();
  const n = (k: string) => {
    const v = parseFloat(s(k));
    return Number.isFinite(v) && v > 0 ? v : null;
  };
  return {
    address: s('address'), city: s('city'), state: s('state') || 'CA', zip: s('zip') || null,
    buildingSf: n('building_sf'), lotSizeAcres: n('acres'),
  };
};

/** ⭐ The 114th's review: what the correction note compares - the filing facts plus his lot SF, which files nothing. */
type NoteFacts = LoiWouldCreate & { lotSf: number | null };
const noteFactsOf = (form: HTMLFormElement | null): NoteFacts => {
  const v = parseFloat(((form ? (new FormData(form).get('lot_sf') as string | null) : null) ?? '').trim());
  return { ...factsOf(form), lotSf: Number.isFinite(v) && v > 0 ? v : null };
};
/** The boxes the note reads that the filing lookup does not - a change to one re-reads the note, never the filing. */
const NOTE_ONLY_INPUTS: readonly string[] = ['lot_sf'];

/**
 * ⭐ What the lookup's DECISION depends on: the address, the city, the state. The size and ZIP only
 *   change what a new record would HOLD - and those are always shown and sent live from the form.
 */
const sigOf = (f: LoiWouldCreate): string =>
  JSON.stringify([f.address.toLowerCase(), f.city.toLowerCase(), (f.state || 'CA').toLowerCase()]);

const pct = (n: number): string => `${Math.round(n * 100)}%`;

/** No asks from the chat - one shared list, so an absent prop is the same value on every render. */
const NO_ASKS: readonly string[] = [];

/** One section ask from the chat for this letter: `key` says whether it was drafted; `words` are his, verbatim. */
interface ChatAsk { key: string; words: string }

/**
 * ⭐ THE CHAT'S "CHANGE ..." WHILE A LETTER IS OPEN (`revise_open_loi`). Rendered only inside the open, unfinished
 *   letter, so with none open nothing answers and the rail SAYS so.
 * ⛔ Registered where the ask will look - `PageActionContext.run` never crosses the two maps: a letter on a record page
 *   answers an ask made on that record; a recordless letter (the rail's own host) answers an ask made with no record.
 */
function ReviseOpenLoiDoor({ onInstruction }: { onInstruction: (words: string) => void }) {
  usePageAction('revise_open_loi', (fields) => { if (fields.instruction) onInstruction(String(fields.instruction)); });
  return null;
}
function ReviseRecordlessLoiDoor({ onInstruction }: { onInstruction: (words: string) => void }) {
  usePageActionFallback('revise_open_loi', (fields) => { if (fields.instruction) onInstruction(String(fields.instruction)); });
  return null;
}

export default function LoiGeneratorDialog({
  open,
  onClose,
  prefill,
  dealId,
  onFiled,
  onDealPriceChanged,
  recordless = false,
}: {
  open: boolean;
  onClose: () => void;
  prefill?: LoiPrefill;
  /** When present the letter is filed against this deal. */
  dealId?: string | null;
  /** Called after a letter is filed, so the caller can refresh its document list. */
  onFiled?: () => void;
  /**
   * ⛔ I1: "Make this the deal's number" changed the DEAL's price from inside this dialog. The page holding the deal
   *   must take it - its Save sends the whole deal, so a stale copy writes the old price back.
   * ⛔ B1: `dealId` names the deal it was changed on - a late answer must never land on another.
   */
  onDealPriceChanged?: (offerPrice: number, dealId: string) => void;
  /**
   * Set only by `RecordlessLoiHost`: no record stands behind this letter, so the chat's change to it comes from an ask
   * made with no record on screen - the rail's own map, never a page's.
   */
  recordless?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * ⚠️ `formKey` REMOUNTS THE FORM when a plan arrives, and it is not decoration. Every input here
   *   is UNCONTROLLED with a `defaultValue`, which React applies ONLY ON MOUNT - so without the key
   *   the read would succeed, the state would update, and not one field on screen would move.
   */
  const [docPlan, setDocPlan] = useState<LoiPrefill | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [docs, setDocs] = useState<{ id: string; filename: string }[]>([]);
  const [reading, setReading] = useState(false);
  /**
   * ⛔⛔⛔ EVERY INPUT READS `merged`, NEVER `prefill`. Found by LOOKING, 2026-09-17: the notices said
   *    "purchasePrice: “$4,550,000” (handwritten)" while the price input still showed the deal's
   *    $1,500,000 and the EMD sat empty - `price` and `emd` read `prefill`, which the document plan
   *    never touches, so the whole "fields fill from the counter" door filled nothing. The guard pins
   *    it: no input in this file may take its default from the bare prefill object.
   */
  const merged: LoiPrefill | undefined = docPlan ? { ...(prefill ?? {}), ...docPlan } : prefill;
  const [result, setResult] = useState<GeneratedLoi | null>(null);

  /**
   * ⭐⭐ THE 100TH - FILING A RECORDLESS LETTER, his 97th choice: *"create the property record and
   *    file it."* ⛔ OFF by default - LP1.5's ruling, filing is ASKED FOR - and offered only when
   *    there is no deal, because a deal files into its own Deal Room as it always has.
   */
  const formRef = useRef<HTMLFormElement | null>(null);
  /**
   * ⭐ THE LOI OVERRIDE ("say it my way", spec 3.6): his changes to THIS letter, previewed section by section and held
   *   here until Generate. The preview reads the form through the same reader Generate does, so it is the same letter;
   *   "Always do it this way" reads it through this one too.
   */
  const readLetter = useCallback(() => (formRef.current ? readLoiInput(formRef.current) : null), []);
  const ov = useLoiOverrides(readLetter);
  /**
   * ⭐ THE 112TH - HIS ANSWER 2026-09-30: the chat must be usable BESIDE this letter (the chat's `revise_open_loi` answers
   *   only while it is open). Measured on 13368 Main St: this overlay covered the docked rail at 1280, and at 375 a letter
   *   the chat opened landed hidden under its sheet. So the letter tells the rail it is open - it comes to the front, and
   *   the two then swap rather than one covering the other - stops its overlay at the docked rail, and offers the chat
   *   itself (the top bar's opener is under the overlay). `null` with no Layout around it: nothing changes.
   */
  const rail = useAssistantRail();
  const letterOpened = rail?.letterOpened;
  useEffect(() => (open && letterOpened ? letterOpened() : undefined), [open, letterOpened]);
  /**
   * ⭐ The review (I1): once Generate FINISHES the letter its change door closes - and the chat, beside it, said "No LOI is
   *   open". It says the letter is finished only if the letter says so.
   */
  const finished = Boolean(result);
  const letterFinished = rail?.letterFinished;
  useEffect(() => (open && finished && letterFinished ? letterFinished() : undefined), [open, finished, letterFinished]);
  /** The rail is docked beside the page from 1280 - everywhere but the map page, where it overlays at any width. */
  const railDocksBeside = Boolean(rail?.open && !rail.fullBleed);
  /**
   * ⭐ HIS SECTION ASKS FROM THE CHAT - those he opened the letter with (`sectionInstructions`) and each later
   *   "change ..." (`revise_open_loi`). Each is drafted against the letter ON SCREEN, one after another in his order, and
   *   waits for his Accept. Until the letter can be built they WAIT, named, so none is lost in silence.
   * ⛔ Drafted ONCE per letter: what was drafted is forgotten when the dialog closes - so a re-render or the same asks
   *   sent again draft nothing new, and the NEXT letter drafts its own asks even word for word the last one's.
   */
  const openedWith = prefill?.sectionInstructions ?? NO_ASKS;
  /**
   * ⛔⛔ I1 (the 113th's review): HIS OPENING ASKS ARE LATCHED. Every host REPLACES the prefill on each "open the letter"
   *    from the chat, and an ask read only from the prop vanished with it - Generate stopped waiting and the letter filed
   *    without it. Latched during render (never an effect, which would paint one frame without them): a prefill can only
   *    ADD asks; they go when the letter closes, with the rest of it.
   */
  const [opened, setOpened] = useState<ChatAsk[]>([]);
  const openedNew = open ? [...new Set(openedWith)].filter((w) => !opened.some((a) => a.key === `opened:${w}`)) : [];
  if (openedNew.length) setOpened([...opened, ...openedNew.map((words) => ({ key: `opened:${words}`, words }))]);
  const [later, setLater] = useState<ChatAsk[]>([]);
  const laterNo = useRef(0);
  const drafted = useRef(new Set<string>());
  /** Moves when the letter closes: an ask still queued for it is dropped, so it never lands in the next letter. */
  const letterRun = useRef(0);
  /** Every chat draft of this letter runs after the one before it - in his order, never two at once. */
  const askChain = useRef<Promise<void>>(Promise.resolve());
  const chatAsks: ChatAsk[] = [...opened, ...later];
  const chatAsksKey = JSON.stringify(chatAsks.map((a) => a.key));
  /** Ruling 27b: his own edits of the letter's price box, counted - the what-if's note about the box goes when this moves. */
  const [priceBoxEdits, setPriceBoxEdits] = useState(0);
  /**
   * ⭐ The correction note (spec 3.9): the building facts the letter's boxes hold NOW - read when the form mounts or a
   *   counter read remounts it, and on each edit of a building box - to hold against the record's (`recordFacts`).
   */
  const [letterFacts, setLetterFacts] = useState<{ form: number; facts: NoteFacts } | null>(null);
  /**
   * ⛔ The 113th's review: a reading belongs to the FORM it was read from. A counter read remounts the form, and facts
   *   read off the old one painted the old note for a frame ("Your 42,000 SF..." beside a box reading 40,000) - so a
   *   reading is tagged with its form, and only the form on screen speaks. Every reader goes through here: the mount and
   *   remount, his edit of a building box, and the OM read (which fills boxes without a keystroke - M2).
   */
  const formKeyNow = useRef(formKey);
  formKeyNow.current = formKey;
  const readLetterFacts = () => setLetterFacts({ form: formKeyNow.current, facts: noteFactsOf(formRef.current) });
  /**
   * ⭐ D6 - HIS HOUSE STYLE APPLIES VISIBLY (spec 3.8), previewed or not: his rules for the letter's wording, read when the
   *   dialog opens and when he changes the wording, and counted in one line near Generate. ⛔ A read for an older
   *   wording (or the last letter) never lands; a read that fails says nothing here - the preview still lists every rule.
   */
  const [houseRules, setHouseRules] = useState<HouseStyleRule[] | null>(null);
  const houseAsk = useRef(0);
  const wordingNow = (): 'september_2026' | 'june_2026' =>
    (formRef.current?.elements.namedItem('wording') as HTMLSelectElement | null)?.value === 'june_2026' ? 'june_2026' : 'september_2026';
  const readHouseStyle = useCallback((era: 'september_2026' | 'june_2026') => {
    const asked = ++houseAsk.current;
    void (async () => {
      try {
        const res = await api.getLoiHouseStyle(era);
        if (asked === houseAsk.current) setHouseRules(Array.isArray(res?.rules) ? res.rules : []);
      } catch {
        if (asked === houseAsk.current) setHouseRules(null);
      }
    })();
  }, []);
  const [fileIt, setFileIt] = useState(false);
  const [filingPlan, setFilingPlan] = useState<LoiFilingPlan | null>(null);
  /** The facts the plan was computed FOR. A plan for another address is a plan for another building. */
  const [planFor, setPlanFor] = useState<string | null>(null);
  /** The filing facts on screen NOW, kept current so staleness is SHOWN, not only enforced. */
  const [liveFacts, setLiveFacts] = useState<LoiWouldCreate | null>(null);
  const [planBusy, setPlanBusy] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  /** Ask band only: a candidate's property id, or 'new'. ⛔ Nothing pre-selected - "shows me both and asks". */
  const [filingChoice, setFilingChoice] = useState<string | null>(null);
  /** Why Generate stopped to ask about filing - shown BESIDE the question, not at the top of a long form. */
  const [filingNotice, setFilingNotice] = useState<string | null>(null);

  const [omRead, setOmRead] = useState<(LoiOmRead & { filename: string; filled: string[]; kept: string[] }) | null>(null);
  const [omBusy, setOmBusy] = useState(false);
  const [omError, setOmError] = useState<string | null>(null);

  const planStale = Boolean(filingPlan && liveFacts && planFor !== sigOf(liveFacts));

  const checkFiling = async (facts: LoiWouldCreate) => {
    setPlanBusy(true);
    setPlanError(null);
    try {
      const res = await api.loiFilingPlan(facts);
      setFilingPlan(res.plan);
      setPlanFor(sigOf(facts));
      setFilingChoice(null);
    } catch (err) {
      setFilingPlan(null);
      setPlanFor(null);
      setPlanError(err instanceof Error ? err.message : 'Could not check your property database');
    } finally {
      setPlanBusy(false);
    }
  };

  const toggleFileIt = (on: boolean) => {
    setFileIt(on);
    setFilingNotice(null);
    if (!on) return;
    const facts = factsOf(formRef.current);
    setLiveFacts(facts);
    void checkFiling(facts);
  };

  /**
   * ⭐⭐ THE OM FILLS ONLY WHAT IS EMPTY, IN PLACE. What he typed - and anything from his own words
   *    in chat - always beats a seller's marketing package. So there is no remount (which would also
   *    wipe everything else he had typed, the 88th's lesson) and no change to `merged`: each input is
   *    checked where it stands and filled only if it holds nothing.
   * ⚠️ A counter read remounts the form (`formKey`), which drops values filled here. That door
   *   exists only on a deal, and a counter is the newer, more specific document.
   */
  const applyOmRead = (read: LoiOmRead, filename: string) => {
    const form = formRef.current;
    const filled: string[] = [];
    const kept: string[] = [];
    for (const [key, names, label] of OM_TO_INPUTS) {
      const v = read.prefill[key];
      if (v === undefined || v === null || v === '') continue;
      let wrote = false;
      let found = false;
      for (const name of names) {
        const el = form?.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement | null;
        if (!el) continue;
        if (el.value.trim()) { found = true; continue; }
        el.value = RECASE_WHEN_SHOUTED.has(key) ? unshout(String(v)) : String(v);
        wrote = true;
      }
      if (wrote) filled.push(label);
      else if (found) kept.push(label);
    }
    setOmRead({ ...read, filename, filled, kept });
    // Setting `.value` fires no React onChange, so the filing lookup must be told the facts moved.
    if (fileIt) setLiveFacts(factsOf(form));
    readLetterFacts();
    // ...and so must the preview: a filled box is a different letter from the one it showed.
    if (filled.length > 0) ov.markStale();
  };

  const readOm = async (source: Parameters<typeof api.loiOmFacts>[0], filename: string) => {
    setOmBusy(true);
    setOmError(null);
    try {
      const res = await api.loiOmFacts(source);
      applyOmRead(res.read, filename);
    } catch (err) {
      setOmError(err instanceof Error ? err.message : 'Could not read that offering memorandum');
    } finally {
      setOmBusy(false);
    }
  };

  const onOmFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const base64 = await fileToBase64(file);
      await readOm({ document: { filename: file.name, base64 } }, file.name);
    } catch (err) {
      setOmError(err instanceof Error ? err.message : `Could not read ${file.name}`);
    }
  };

  /**
   * Lock the page behind the dialog. Without this the property page keeps scrolling under the
   * overlay - the map and photos slide past behind a form that is supposed to be modal, which
   * is exactly what it looked like: "the map is still showing in the middle of the page".
   * The scroll POSITION is preserved (and restored) rather than reset, so closing the dialog
   * puts the operator back where he was.
   */
  /** ⛔ Resets on close: a suggestion read for the LAST letter must not survive into the next one. */
  useEffect(() => {
    if (!open) {
      setDocPlan(null);
      /**
       * ⛔⛔ B2: THE LETTER ITSELF IS FORGOTTEN HERE, however the dialog closed - its own buttons, or its HOST (the deal page
       *    closes it when he moves to another deal). Only `close()` did this, so a host's close carried his changes, an
       *    error and the finished letter's screen into the next letter.
       */
      setResult(null);
      setError(null);
      ov.reset();
      // ⛔ A filing question or an OM read for the LAST letter must not survive into the next one.
      setFileIt(false);
      setFilingPlan(null);
      setPlanFor(null);
      setLiveFacts(null);
      setPlanError(null);
      setFilingChoice(null);
      setFilingNotice(null);
      setOmRead(null);
      setOmError(null);
      // ⛔ His asks from the chat belong to THIS letter: forgotten with it, and any still queued are dropped.
      setLater([]);
      setOpened([]);
      setLetterFacts(null);
      drafted.current = new Set();
      letterRun.current += 1;
      askChain.current = Promise.resolve();
      // D6: the last letter's house-style count goes with it, and a read still out for it never lands.
      houseAsk.current += 1;
      setHouseRules(null);
      return;
    }
    if (!dealId) return;
    let cancelled = false;
    (async () => {
      try {
        const room = await api.getDealRoom(dealId);
        const list = (room?.documents ?? room?.files ?? room?.room?.documents ?? []) as any[];
        if (!cancelled) {
          setDocs(
            list
              .map((d) => ({ id: String(d.file_id ?? d.id ?? ''), filename: String(d.filename ?? d.file_name ?? '') }))
              .filter((d) => d.id && d.filename)
          );
        }
      } catch {
        // ⚠️ A picker that cannot load its list is an empty picker, not a broken dialog.
        if (!cancelled) setDocs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, dealId]);

  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const html = document.documentElement;
    const prev = { bodyOverflow: body.style.overflow, bodyPad: body.style.paddingRight, htmlOverflow: html.style.overflow };
    // BOTH elements, deliberately. `html` is the scrolling element here (measured), and
    // body-only relies on CSS overflow PROPAGATION to the viewport - true per spec, but only
    // while html's own overflow stays visible. Locking both removes that dependency.
    const gap = window.innerWidth - html.clientWidth;
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    if (gap > 0) body.style.paddingRight = `${gap}px`;
    // ⭐ The review (M3): the padding moves the page - and the docked rail - in by `gap`; the overlay stops short of it too.
    if (gap > 0) html.style.setProperty('--scroll-lock-gap', `${gap}px`);
    return () => {
      body.style.overflow = prev.bodyOverflow;
      body.style.paddingRight = prev.bodyPad;
      html.style.overflow = prev.htmlOverflow;
      html.style.removeProperty('--scroll-lock-gap');
    };
  }, [open]);

  // D6: read on open - and after a counter read remounts the form, whose wording box then shows its default again.
  useEffect(() => {
    if (open) readHouseStyle(wordingNow());
  }, [open, formKey]);
  // The correction note: what the boxes hold once the form is on screen, and again after a counter read remounts it.
  useEffect(() => {
    if (open) readLetterFacts();
  }, [open, formKey]);

  // His asks need a letter to be drafted against: build it once when they arrive. A refusal is shown and NOT retried -
  // they wait, named, until he previews it himself (or the chat sends another change, which tries again).
  useEffect(() => {
    if (open && chatAsks.length > 0 && !ov.preview && ov.busy === null && !ov.error) void ov.refresh();
  }, [open, chatAsks.length, ov.preview, ov.busy, ov.error]);
  useEffect(() => {
    if (!open || !ov.preview) return;
    const run = letterRun.current;
    for (const a of chatAsks) {
      if (drafted.current.has(a.key)) continue;
      drafted.current.add(a.key);
      askChain.current = askChain.current.then(async () => { if (run === letterRun.current) await ov.draft(a.words, null, 'chat'); });
    }
  }, [open, ov.preview, chatAsksKey]);

  if (!open) return null;

  /** ⭐ The correction note (spec 3.9): where the letter states a building fact differently from its record. */
  const factsOnScreen = letterFacts && letterFacts.form === formKey ? letterFacts.facts : null;
  const corrections = correctionNotes(prefill?.recordFacts, factsOnScreen ?? { address: '', city: '', zip: null, buildingSf: null });

  /** D6: the sections his house style changes in this letter - the rules he has not set aside, a section counted once. */
  const skippedRules = new Set(ov.skip);
  const houseSections = new Set(
    (houseRules ?? []).filter((r) => !skippedRules.has(r.id)).map((r) => (r.action === 'add_after' ? r.newKey ?? `new:${r.id}` : r.section)),
  ).size;

  /**
   * ⛔⛔ GENERATE WAITS WHILE A CHANGE OF HIS IS NOT YET IN THE LETTER (B3, widened by his answer 2026-09-30, the 113th):
   *    being drafted, waiting to be drafted (a chat ask not yet handed to the drafter - until the letter can be built,
   *    and in the moment between the first preview landing and its draft starting, which the 113th's review found free),
   *    or drafted and waiting for his Accept - from the chat or his own Change box. Filing then filed the letter without it, and nothing said so; his
   *    ruling is that a filed letter never silently lacks his change. Why it waits is said BESIDE the button (Ruling 33),
   *    and the same words gate `submit`, so no path round the button files either.
   */
  const generateWaits: string | null =
    ov.busy === 'draft' || chatAsks.some((a) => !drafted.current.has(a.key))
      ? 'Generate waits until your change is drafted.'
      : ov.pending.length > 0
        ? `Generate waits until you accept or discard the drafted change${ov.pending.length === 1 ? '' : 's'} above.`
        : null;

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // ⛔ Never while a change of his is not yet in the letter - it would be filed without it (the button waits too).
    if (busy || generateWaits !== null) return;
    setBusy(true);
    setError(null);

    const fd = new FormData(e.currentTarget);
    const g = (n: string): string => ((fd.get(n) as string) ?? '').trim();
    const num = (n: string): number | undefined => {
      const v = parseFloat(g(n));
      return Number.isFinite(v) && v > 0 ? v : undefined;
    };
    // ⭐ His accepted changes ride with the letter; a draft he has not accepted does not.
    const loi: LoiGenerateInput = { ...readLoiInput(e.currentTarget), sectionOverrides: ov.overrides };

    /**
     * ⛔⛔ FILING IS DECIDED ON WHAT IS SHOWN. If the address moved since the lookup ran, or it never
     *    ran, this checks now and STOPS - he sees the answer before anything is written. The server
     *    re-runs the lookup once more at the moment it writes; this is the half that lets him look.
     */
    let filing: LoiFilingRequest | undefined;
    if (!dealId && fileIt) {
      const facts: LoiWouldCreate = {
        address: g('address'), city: g('city'), state: g('state') || 'CA', zip: g('zip') || null,
        buildingSf: num('building_sf') ?? null, lotSizeAcres: num('acres') ?? null,
      };
      setFilingNotice(null);
      if (!filingPlan || planFor !== sigOf(facts)) {
        setLiveFacts(facts);
        await checkFiling(facts);
        setFilingNotice('Check where this letter will be filed, just above, then generate again.');
        setBusy(false);
        return;
      }
      if (filingPlan.decision === 'refuse') {
        setFilingNotice('Untick "File this letter" to generate it without filing, or fix what is named above.');
        setBusy(false);
        return;
      }
      if (filingPlan.decision === 'ask' && !filingChoice) {
        setFilingNotice('Choose where to file it - one of your existing records, or a new one.');
        setBusy(false);
        return;
      }
      if (filingPlan.decision === 'file_on_existing' && filingPlan.match) {
        filing = { fileToPropertyId: filingPlan.match.propertyId };
      } else if (filingPlan.decision === 'ask' && filingChoice && filingChoice !== 'new') {
        filing = { fileToPropertyId: filingChoice };
      } else if (filingPlan.decision === 'create' || (filingPlan.decision === 'ask' && filingChoice === 'new')) {
        filing = { createProperty: facts };
      }
    }

    try {
      // ⛔ The same house-style skips the preview used, so the filed letter is the one he looked at.
      const res = await api.generateLoi(loi, dealId ?? undefined, filing, ov.skip);
      setResult(res.loi);
      if (res.loi.filed) onFiled?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate the LOI');
    } finally {
      setBusy(false);
    }
  };

  const downloadDocx = () => {
    if (!result?.docx_base64) return;
    const bytes = Uint8Array.from(atob(result.docx_base64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.filename || 'LOI.docx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // MOB1 (the 126th): the print window carries its way back - on his phone it had none.
  const print = () => {
    if (result) printInOwnWindow(result.loi_html);
  };

  // ⛔ This letter's changes, error and result must not ride into the next one - forgotten when `open` goes false (B2).
  const close = () => onClose();

  /** "Use this price in the letter": this letter's price box only. Setting `.value` fires no change event, so say it. */
  const putWhatIfPrice = (price: number) => {
    const el = formRef.current?.elements.namedItem('price') as HTMLInputElement | null;
    if (el) { el.value = String(price); ov.markStale(); }
  };

  /** `revise_open_loi`: his words join this letter's asks. No letter built yet - build it now; if it cannot be, they wait. */
  const reviseFromChat = (words: string) => {
    // Ruling 27c: the key is taken HERE, once - a ref bumped inside a state updater is impure (StrictMode runs it twice).
    const key = `later:${++laterNo.current}`;
    setLater((l) => [...l, { key, words }]);
    if (!ov.preview && ov.busy === null) void ov.refresh();
  };

  return (
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center z-[1200] p-4${railDocksBeside ? ` ${BESIDE_DOCKED_RAIL}` : ''}`}
      /* z-[1200], NOT z-50: Leaflet gives its own panes z-index 400-650 (marker 600, tooltip
         650), and those compete directly with a fixed overlay - at z-50 the property map drew
         straight THROUGH this dialog. Same level as the Prep Call modal, which sits above the
         map on this page for the same reason.
         ⭐ The 112th: with the rail docked open it stops at the rail's edge from 1280 - the chat is beside the letter,
         never under it. Closed, the overlay covers the whole screen again. */
    >
      {/* ⛔ Only while the letter is still being written: once generated it is finished, and a change asked then is
            told no letter is open rather than drafted into one already filed. */}
      {!result && (recordless
        ? <ReviseRecordlessLoiDoor onInstruction={reviseFromChat} />
        : <ReviseOpenLoiDoor onInstruction={reviseFromChat} />)}
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{result ? 'Letter of Intent' : 'Generate Letter of Intent'}</CardTitle>
            <div className="flex items-center gap-2">
              {/* ⭐ The 112th: the chat, from the letter itself - the top bar's opener is under this overlay. From 1280,
                    with the rail docked open beside the letter, there is nothing to open. */}
              {rail && (
                <Button type="button" variant="outline" size="sm" onClick={() => rail.showChat()} className={railDocksBeside ? 'xl:hidden' : ''}>
                  Assistant
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={close}>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          {result ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Button onClick={downloadDocx}>Download Word (.docx)</Button>
                <Button variant="outline" onClick={print}>Print / PDF</Button>
                <Button variant="ghost" onClick={close}>Done</Button>
              </div>
              {/*
                ⛔⛔ THREE DIFFERENT PLACES A LETTER CAN BE, AND THE SENTENCE MUST NAME THE RIGHT ONE.
                   Before the 100th `filed` could only mean the Deal Room, so a letter filed on a
                   PROPERTY would have read "Filed on this deal" on a screen with no deal at all.
              */}
              <div data-filing-result className="text-xs text-gray-600 space-y-1">
                {result.filing ? (
                  <>
                    <p>
                      Filed on <span className="font-semibold">{result.filing.filed_on}</span> in your
                      property database.
                      {result.filing.created_property ? ' A new record was created for it.' : ''}
                      {result.filing.already_filed ? ' This exact letter was already filed there - nothing new was written.' : ''}
                    </p>
                    {result.filing.changed_from_plan && (
                      <p className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-900">
                        {result.filing.changed_from_plan}
                      </p>
                    )}
                  </>
                ) : result.filing_refused ? (
                  <p className="rounded border border-red-200 bg-red-50 px-2 py-1 text-red-800">
                    Not filed: {result.filing_refused} Download the Word file to keep it.
                  </p>
                ) : (
                  <p className="text-gray-500">
                    {result.filed
                      ? 'Filed on this deal - it is in the Deal Room documents list.'
                      : 'Not filed anywhere - download the Word file to keep it.'}
                  </p>
                )}
                {/* ⛔ D1: the letter is filed and fine - but the record of WHY it reads differently was not saved; say so. */}
                {result.changes_recorded === false && (
                  <p className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-900">
                    Filed - but the list of your changes could not be recorded.
                  </p>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-4 max-h-[45vh] overflow-y-auto">
                <pre className="whitespace-pre-wrap text-xs text-gray-700 font-sans">{result.loi_text}</pre>
              </div>
            </div>
          ) : (
            <form
              key={formKey}
              ref={formRef}
              onSubmit={submit}
              // ⛔ His answer 2026-09-30: Enter in the letter's own boxes never files it - only Generate does.
              onKeyDown={enterNeverFiles}
              onChange={(e) => {
                const name = (e.target as HTMLInputElement).name;
                if (fileIt && FILING_INPUTS.includes(name)) setLiveFacts(factsOf(formRef.current));
                if (FILING_INPUTS.includes(name) || NOTE_ONLY_INPUTS.includes(name)) readLetterFacts();
                /**
                 * ⛔ Only a NAMED control is the letter - the reader reads the form by name. The preview's own boxes
                 *   ("Change this") have none and sit inside this form, so typing an instruction is not a change.
                 */
                if (name) ov.markStale();
                // ⛔ Ruling 27b: HIS edit of the price box ends the what-if's note about it ("Use this price" sets the
                //   value without a change event, so it never counts here).
                if (name === 'price') setPriceBoxEdits((n) => n + 1);
                // D6: another wording is another list of his house style.
                if (name === 'wording') readHouseStyle(wordingNow());
              }}
              className="space-y-4"
            >
              {/*
                ⭐⭐ THE DOOR FOR HIS ASK: *"I need to make a new counter based on the email we
                received."* Pick the seller's counter out of this deal's documents and the fields
                below fill from it, each traceable to the sentence it came from.
                ⛔ It SUGGESTS. Nothing is sent, nothing is written, every value stays editable.
                ⛔ The select carries NO `name`, so it cannot reach the submitted FormData.
              */}
              {dealId && (
                <div className="mb-4 rounded border border-gray-200 bg-gray-50 p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Fill from a seller counter:</span>
                    <select
                      className="min-w-[16rem] rounded border-gray-300 text-sm"
                      value=""
                      disabled={reading}
                      onChange={async (e) => {
                        const fileId = e.target.value;
                        if (!fileId) return;
                        setReading(true);
                        setError(null);
                        try {
                          const r = await api.planLoiCounter(fileId, { isAnsweringACounter: true });
                          const pl = r.plan;
                          setDocPlan({
                            purchasePrice: pl.purchasePrice?.value ?? null,
                            emdAmount: pl.emdAmount?.value ?? null,
                            expiration: pl.expiration?.value ?? null,
                            sellerName: pl.sellerName?.value ?? null,
                            counterToSellerCounterNo: pl.counterToSellerCounterNo?.value ?? null,
                            counterNotices: {
                              filename: r.filename,
                              printedLayerIsOurOwnLetter: r.printedLayerIsOurOwnLetter,
                              possibleHandwriting: r.possibleHandwriting,
                              gaps: pl.gaps,
                              held: pl.held.map((h) => ({ field: h.field, reason: h.reason })),
                              superseded: (pl.superseded ?? []).map((h) => ({
                                field: h.field,
                                reason: h.reason,
                                values: h.candidates.map((c) => `${typeof c.value === 'number' ? '$' + c.value.toLocaleString('en-US') : c.value} (page ${c.page})`).join(', '),
                              })),
                              handwrittenButUnverified: (r.handwrittenButUnverified ?? []).map((h) => ({ label: h.label, quote: h.quote, page: h.page })),
                              evidence: Object.entries(pl)
                                .filter(([k, v]) => !['gaps', 'held', 'superseded'].includes(k) && v)
                                .map(([k, v]) => {
                                  const f = v as { source: string; quote?: string; page?: number; readFrom?: string; basis?: string; handwritten?: boolean };
                                  return { field: k, source: f.source, quote: f.quote, page: f.page, readFrom: f.readFrom, basis: f.basis, handwritten: f.handwritten };
                                }),
                            },
                          });
                          // ⚠️ REMOUNT, or the uncontrolled inputs keep their mount-time defaults.
                          setFormKey((k) => k + 1);
                          // A remount fires no change event, and the letter now carries the counter's figures.
                          ov.markStale();
                        } catch (err: any) {
                          setError(err?.message || 'Could not read that document');
                        } finally {
                          setReading(false);
                        }
                      }}
                    >
                      <option value="">{reading ? 'Reading…' : 'Choose a document…'}</option>
                      {docs.map((d) => (
                        <option key={d.id} value={d.id}>{d.filename}</option>
                      ))}
                    </select>
                    {docs.length === 0 && (
                      <span className="text-xs text-gray-500">No documents on this deal yet.</span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Suggests values only - nothing is written or sent, and every field stays editable.
                  </p>
                </div>
              )}
                {/*
                  ⛔⛔ WHAT THE DOCUMENT COULD NOT SETTLE, SAID OUT LOUD - AND SAID RIGHT HERE, under the
                  picker. The picture (2026-09-17) had it at the foot of a 2,400px form: pick a document at the
                  top, and nothing visibly changes where the eye is. A field the reader withheld
                  must explain itself, or the protection reads as a bug and gets switched off.
                */}
                {merged?.counterNotices && (
                  <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm">
                    <div className="font-medium text-amber-900">
                      Read from {merged.counterNotices.filename} - check every figure before sending.
                    </div>
                    {merged.counterNotices.printedLayerIsOurOwnLetter && (
                      <p className="mt-1 text-amber-900">
                        <strong>This looks like a counter written over our own letter.</strong> Its printed
                        figures are the Apex offer, struck through in ink, so they were <strong>not</strong>{' '}
                        used. The seller&rsquo;s terms are the handwriting - the fields below were filled from
                        it where it could be read, so check each one against the page image.
                      </p>
                    )}
                    {merged.counterNotices.superseded.map((s) => (
                      <p key={`superseded-${s.field}`} className="mt-1 text-amber-900">
                        <strong>{s.field}</strong>: printed {s.values} not used - revised by hand.
                      </p>
                    ))}
                    {merged.counterNotices.handwrittenButUnverified.length > 0 && (
                      <p className="mt-1 text-amber-900">
                        Read by hand but not verifiable, so left for you:{' '}
                        {merged.counterNotices.handwrittenButUnverified
                          .map((h) => `${h.label} “${h.quote}” (page ${h.page})`)
                          .join('; ')}.
                      </p>
                    )}
                    {merged.counterNotices.possibleHandwriting && (
                      <p className="mt-1 text-amber-900">
                        Some figures could not be matched to the page text, which is what handwriting looks
                        like. They were not used.
                      </p>
                    )}
                    {merged.counterNotices.gaps.length > 0 && (
                      <p className="mt-1 text-amber-900">
                        Not found in the document, so left for you: {merged.counterNotices.gaps.join(', ')}.
                      </p>
                    )}
                    {merged.counterNotices.held.map((h) => (
                      <p key={h.field} className="mt-1 text-amber-900">
                        <strong>{h.field}</strong> held - {h.reason}
                      </p>
                    ))}
                    {merged.counterNotices.evidence.map((e) => (
                      <p key={e.field} className="mt-1 text-xs text-amber-800">
                        {e.field}: {e.source === 'document'
                          ? `“${e.quote}” (page ${e.page}, ${e.readFrom === 'text' ? 'printed text' : e.handwritten ? 'handwritten on the page image' : 'page image'})`
                          : `inferred - ${e.basis}`}
                      </p>
                    ))}
                  </div>
                )}

              <p className="text-xs text-gray-500 -mt-1">
                Built from the Apex letter format (the Vinod series). The standing language is
                fixed; you fill the deal facts.
                {dealId ? ' The finished letter is filed on this deal.' : ''}
              </p>

              <div className="border-b pb-4">
                <h4 className="font-medium text-gray-900 mb-3">Buyers</h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* DOC4: type a name and it fills from his own contacts - 7,048 of them,
                      synced from GHL and current. ⛔ Still a TEXT field: a buyer is often an
                      ENTITY ("MDTH VICTORVILLE LLC") that is not a contact at all. */}
                  <ContactPicker name="buyer1" required label="Buyer 1" placeholder="Vinod Kardani" defaultValue={merged?.buyerNames?.[0] ?? ''} />
                  <ContactPicker name="buyer2" label="Buyer 2" placeholder="Nayana Kardani" defaultValue={merged?.buyerNames?.[1] ?? ''} />
                  <div className="col-span-1 sm:col-span-2">
                    <Input name="sig_heading" label="Signature heading (optional)" placeholder="Vinod & Nayana Kardani" defaultValue={merged?.signatureHeading ?? ''} />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Buyer profile paragraph (optional)
                  </label>
                  <textarea
                    name="buyer_profile"
                    rows={2}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                    placeholder="Leave blank for your standard paragraph - the 1031 wording below, or on a non-1031 offer the one for how the balance is paid."
                  />
                  <p className="text-[11px] text-gray-400 mt-1">
                    Only your own sentences are written about your buyers - your 1031 wording, or your new-loan,
                    seller-financing or all-cash sentence. Write here to replace it.
                  </p>
                </div>
                {/* ⭐⭐ THE 97TH, AND HIS CATCH. Both sentences below are his own, word for word:
                    all-cash is the Olive Garden letters (Sep 2026), financed is Harbor Freight
                    (Jun 2026). The old builder always used the financed one, which promises a
                    lender pre-qualification letter - wrong on an all-cash offer, and it weakened
                    the offer. Measured across the whole corpus: nothing in the price block can
                    tell the two apart, so this is a choice and never an inference. */}
                <div className="mt-3">
                  <label htmlFor="buyer_profile_variant" className="block text-sm font-medium text-gray-700 mb-1">
                    Buyer Profile wording (1031 offers)
                  </label>
                  <select
                    id="buyer_profile_variant"
                    name="buyer_profile_variant"
                    defaultValue={merged?.buyerProfile ?? ''}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                  >
                    <option value="">All cash (default) - not contingent on financing of any kind</option>
                    <option value="financed_1031">Financed - proof of funds and lender pre-qualification letter</option>
                    <option value="none">No Buyer Profile paragraph</option>
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">
                    A non-1031 offer follows the balance in the price block instead: new loan, seller financing, or all cash
                    (the whole price, or a down payment with a cash balance).
                    With no down payment and no all-cash choice it prints none.
                    &ldquo;No Buyer Profile paragraph&rdquo; leaves it out either way.
                  </p>
                </div>
              </div>

              <div className="border-b pb-4">
                <h4 className="font-medium text-gray-900 mb-3">Seller / Recipient</h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input name="seller_name" required label="Seller (entity or person) *" placeholder="MDTH VICTORVILLE LLC" defaultValue={merged?.sellerName ?? ''} />
                  {/* ⭐ The 97th. His Olive Garden letter heads "Owner of Record - 582 N Lone Hill
                      Avenue" and greets "Dear Mr. Son:" - the listing agent. Before this the
                      greeting was hard-wired to the seller, so an unnamed owner produced "Dear
                      Owner of Record - 82894 CA-111:" and he edited the .docx by hand every time. */}
                  <Input
                    name="salutation_name"
                    label="Greet (defaults to the seller)"
                    placeholder="Mr. Narchi"
                    defaultValue={merged?.salutationName ?? ''}
                  />
                  <Input name="care_of" label="c/o (listing brokerage)" placeholder="Berkeley Capital Advisors" defaultValue={merged?.careOf ?? merged?.sellersBrokerage ?? ''} />
                  <Input name="attn" label="Attn" placeholder="Listing agent name" defaultValue={merged?.listingAttn ?? ''} />
                  <Input name="sellers_brokerage" label="Seller's brokerage (for the Brokerage section)" placeholder="Berkeley Capital Advisors" defaultValue={merged?.sellersBrokerage ?? ''} />
                  <Input name="seller_signer1" label="Seller signer 1 (defaults to the seller)" placeholder="Larry Aguilera" />
                  <Input name="seller_signer2" label="Seller signer 2 (only if two people sign)" />
                </div>
                {/* ⛔⛔ THE LABEL IS THE GATE. Without it the operator reads a machine's guess as a
                    checked fact, and the one thing between an extraction and a counterparty is
                    that he looked. Shown ONLY when something was actually extracted. */}
                {merged?.brokerSource && (merged.sellersBrokerage || merged.listingAttn) && (
                  <div data-broker-source className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                    {/* DOC3b: WHOSE value is it? A value he confirmed and one a language model
                        read off a marketing document are not the same claim, and giving both the
                        same words trains him to skip the one that matters. */}
                    {merged.brokerSource === 'operator' ? (
                      <p>
                        <span className="font-semibold">From a letter you sent.</span>{' '}
                        You confirmed this, so it is used instead of whatever the documents say.
                      </p>
                    ) : merged.brokerSource === 'pdf_extract' ? (
                      <p>
                        <span className="font-semibold">Read from the offering memorandum - please confirm.</span>{' '}
                        Nothing here has been verified against a title profile.
                      </p>
                    ) : (
                      <p>
                        <span className="font-semibold">Read from a document - please confirm.</span>{' '}
                        Source: {merged.brokerSource}.
                      </p>
                    )}
                    {merged.listingBroker && (
                      <p className="mt-1 text-amber-800">
                        {/* ⭐ The OM's FIRM is dropped once he has confirmed his own - printing it
                            under "you confirmed this" contradicts the field just above. The PERSON
                            stays: a name, phone and email are only ever known from the OM, and they
                            are what he needs to reach the other side. Found by looking. */}
                        {[
                          merged.listingBroker.agentName,
                          merged.brokerSource === 'operator' ? null : merged.listingBroker.brokerage,
                          merged.listingBroker.agentPhone,
                          merged.listingBroker.agentEmail,
                        ].filter(Boolean).join(' · ')}
                        {/* ⛔ The licence is shown ONLY when it survived `listingBroker.ts`, which
                            drops one that arrived without a named person - in an OM a licence is
                            usually the firm's, printed in a copyright footer beside a domain. */}
                        {merged.listingBroker.agentLicense && (
                          <span> · Lic {merged.listingBroker.agentLicense}</span>
                        )}
                      </p>
                    )}
                  </div>
                )}
                <label className="flex items-start gap-2 text-sm text-gray-700 mt-3">
                  <input type="checkbox" name="seller_is_entity" defaultChecked={merged?.sellerIsEntity ?? false} className="rounded border-gray-300 mt-0.5" />
                  <span>
                    Seller signs as a company / trust
                    <span className="block text-[11px] text-gray-400">
                      Signature block becomes "By: ___ / Date" over a "Name / Title" line, the way
                      an LLC signs. Leave unticked when a person signs their own name.
                    </span>
                  </span>
                </label>
                <p className="text-[11px] text-gray-400 mt-2">
                  Each named signer gets one signature line. Leave a slot blank and no empty
                  line is printed for it.
                </p>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lines under the c/o - a mailing address, or the agent&rsquo;s email and phone (one per row)</label>
                  <textarea
                    name="address_lines"
                    rows={2}
                    defaultValue={(merged?.recipientLines ?? []).join('\n')}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                    placeholder={'1228 East Morehead Street, Suite 200\nCharlotte, NC 28204'}
                  />
                </div>
              </div>

              <div className="border-b pb-4">
                <h4 className="font-medium text-gray-900 mb-3">Property</h4>
                {/*
                  ⭐⭐ HIS 97TH CHOICE: the descriptive facts may be read from an OM he attaches.
                  ⛔⛔ NEVER the price, the EMD, the expiration, the seller or the cap rate - the server
                     refuses them and this map could not place them anyway. The control NAMES what it
                     fills and what it will not, because a withheld field with no explanation reads
                     as broken rather than protected.
                  ⛔ The file input carries NO `name`, so a PDF can never ride into the submitted form.
                */}
                <div data-om-read className="mb-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
                  <p className="font-medium text-gray-800">Read the property from an offering memorandum</p>
                  <p className="mt-0.5 text-[11px] text-gray-500">
                    Fills only EMPTY boxes: address, city, ZIP, building SF, acres, the RE-line
                    description, Attn and the seller&rsquo;s brokerage. It never reads the price, the
                    deposit, the expiration, the seller or the cap rate - those are yours to set.
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {/*
                      🚨 FOUND BY MEASURING A NARROW PANE (340px): the shared Button is `nowrap` with a
                         fixed height, so "Read facts from OM - Applebees - 82894 CA-111, Indio, CA.pdf"
                         ran 99px past the card, the card scrolled sideways, and the name was cut off
                         mid-word - a control that no longer named its contents. A plain button that
                         WRAPS keeps the whole file name readable at any width.
                    */}
                    {(merged?.omAttachments ?? []).map((a) => (
                      <button
                        key={a.storagePath}
                        type="button"
                        disabled={omBusy}
                        onClick={() => { void readOm({ attachment: { storage_path: a.storagePath, file_name: a.fileName } }, a.fileName); }}
                        className="max-w-full break-words rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-left text-[11.5px] font-semibold leading-snug text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Read facts from {a.fileName}
                      </button>
                    ))}
                    <label className="inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                      {(merged?.omAttachments ?? []).length ? 'Or choose a PDF…' : 'Choose a PDF…'}
                      <input type="file" accept="application/pdf" className="hidden" disabled={omBusy} onChange={(e) => { void onOmFile(e); }} />
                    </label>
                  </div>
                  {omBusy && <p className="mt-2 text-xs text-gray-600">Reading it - an offering memorandum takes 20-40 seconds.</p>}
                  {omError && <p className="mt-2 text-xs text-red-700">{omError}</p>}
                  {omRead && (
                    <div data-om-result className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 space-y-1">
                      <p className="font-semibold">Read from {omRead.filename} - check every value before sending.</p>
                      <p>
                        {omRead.filled.length
                          ? `Filled: ${omRead.filled.join(', ')}.`
                          : 'Nothing was filled - every box it could fill already had something in it.'}
                      </p>
                      {omRead.kept.length > 0 && (
                        <p>Kept what was already in the form: {omRead.kept.join(', ')}.</p>
                      )}
                      {omRead.withheld.length > 0 && (
                        <p>
                          Not copied - yours to decide: {[...new Set(omRead.withheld.map((w) => w.label))].join(', ')}.
                        </p>
                      )}
                      {omRead.evidence.length > 0 && (
                        <ul className="list-disc pl-4 text-amber-800">
                          {/* ⭐ The FORM's name for each box, the same one "Filled:" uses - found by
                              looking: the bullets said "Listing Agent" and "Land Acres" for the boxes
                              the line above calls Attn and Acres. The quote keeps the OM's own words. */}
                          {omRead.evidence.map((ev) => (
                            <li key={`${ev.field}-${ev.page}-${ev.quote}`}>
                              {OM_TO_INPUTS.find(([key]) => key === ev.field)?.[2] ?? ev.label}: &ldquo;{ev.quote}&rdquo; (p. {ev.page})
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="col-span-2">
                    <Input name="address" required label="Address *" defaultValue={merged?.address ?? ''} />
                  </div>
                  <Input name="city" required label="City *" defaultValue={merged?.city ?? ''} />
                  <Input name="state" label="State" defaultValue={merged?.state ?? 'CA'} />
                  <Input name="zip" label="ZIP" defaultValue={merged?.zip ?? ''} />
                  <Input name="building_sf" type="number" label="Building SF" defaultValue={merged?.buildingSf ?? ''} />
                  {/* ⭐ Prefilled from the 97th on: a recordless letter states its own acreage,
                      and it prints in the RE line. It was the only property input with no
                      defaultValue, so a stated figure reached the form and was dropped. */}
                  <Input name="acres" type="number" step="0.01" label="Acres" defaultValue={merged?.acres ?? ''} />
                  <Input name="county" label="County" defaultValue={merged?.county ?? ''} />
                  <div className="col-span-2 sm:col-span-4">
                    <Input
                      name="description"
                      required
                      label="RE-line description * (how the letter characterizes the building)"
                      placeholder="single-tenant absolute-NNN retail building (Dollar General)"
                      defaultValue={merged?.description ?? ''}
                    />
                  </div>
                  {/* ⭐ THE 101ST - HIS SEPTEMBER RE LINE: "RE: Letter of Intent - Applebee's Grill + Bar,
                      82894 CA-111, ... on +/- 0.28 acres (+/- 12,197 SF), fronting Highway 111 at
                      Smurr Street". Each prints only when filled, so leaving all three empty gives
                      the June line a counter to a June letter needs. */}
                  <div className="col-span-2">
                    <Input name="re_line_tenant" label="Tenant (starts the RE line)" placeholder="Applebee's Grill + Bar" defaultValue={merged?.reLineTenant ?? ''} />
                  </div>
                  <div className="col-span-2">
                    <Input name="lot_sf" type="number" label="Lot SF (after the acres)" placeholder="12197" defaultValue={merged?.lotSf ?? ''} />
                  </div>
                  <div className="col-span-2 sm:col-span-4">
                    <Input
                      name="re_line_location"
                      label="Location (ends the RE line)"
                      placeholder=", fronting Highway 111 at Smurr Street"
                      defaultValue={merged?.reLineLocation ?? ''}
                    />
                  </div>
                </div>
                {/* ⭐ The correction note (spec 3.9): his figure goes in the letter; one line here, never in it, says where
                      that differs from the record. */}
                {corrections.length > 0 && (
                  <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-2 text-[12.5px] text-amber-900" data-testid="loi-corrections">
                    {corrections.map((c) => <p key={c}>{c}</p>)}
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3">Terms</h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Input name="price" type="number" required label="Purchase price *" leftAddon="$" defaultValue={merged?.purchasePrice ?? ''} />
                  {/*
                    ⭐ THE WHAT-IF PRICE (spec 3.9) - on a deal only: the price at a target cap, from the deal's saved
                      analysis. Directly under the price: the next row on a phone; from sm up the whole second row, so
                      the price row keeps its three boxes. "Use this price" writes this letter's price box; the DEAL's
                      price moves only on "Make this the deal's number".
                  */}
                  {dealId && (
                    <div className="sm:col-span-3 sm:row-start-2">
                      <LoiWhatIfPrice dealId={dealId} dealOfferPrice={merged?.dealPrice ?? null} onUsePrice={putWhatIfPrice} onPromoted={onDealPriceChanged} priceBoxEdits={priceBoxEdits} />
                    </div>
                  )}
                  <Input name="down_payment" type="number" label="Cash down (incl. EMD)" leftAddon="$" defaultValue={merged?.downPayment ?? ''} />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Balance paid with</label>
                    <select name="balance_kind" defaultValue={merged?.balanceKind ?? 'cash_balance'} className="w-full rounded-md border-gray-300 shadow-sm text-sm py-2">
                      {/* ⭐ The 97th. Found by generating a real letter: an all-cash offer had
                          no expressible form - the full price as a down payment printed
                          "Cash (Balance): $0". This is his Walgreens Bear Valley block, and it
                          ignores the Down payment box entirely. */}
                      <option value="all_cash">All Cash at Close of Escrow - whole price, no down/balance split</option>
                      <option value="cash_balance">Cash (Balance) - investment/1031 wording</option>
                      <option value="cash">Balance Paid with Cash</option>
                      <option value="new_loan">Balance Paid with New Loan</option>
                      <option value="seller_financing">Balance Paid with Seller Financing</option>
                    </select>
                  </div>
                  <Input name="emd" type="number" required label="Earnest money *" leftAddon="$" defaultValue={merged?.emdAmount ?? ''} />
                  <Input name="dd_days" type="number" label="Due diligence (days)" defaultValue={30} />
                  <Input name="coe_days" type="number" label="Close of escrow (days)" defaultValue={15} />
                  <Input
                    name="expiration_date"
                    type="date"
                    required
                    label="Offer expires *"
                    defaultValue={
                      /^\d{4}-\d{2}-\d{2}$/.test(merged?.expiration ?? '')
                        ? (merged?.expiration as string)
                        : new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10)
                    }
                  />
                  <Input name="escrow_co" label="Escrow / title company" defaultValue="Chicago Title Company" />
                  <Input name="commission" type="number" step="0.25" label="Buyer-side commission %" defaultValue={3} />
                  <Input name="downleg" label="Down-leg COE date (if 1031)" placeholder="7/15/26" defaultValue={merged?.downLegCloseDate ?? ''} />
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Seller-financing terms (one per line)
                    </label>
                    <textarea
                      name="financing_terms"
                      rows={2}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      placeholder={'6% Interest, amortized over 30 years, due in 5 years. No pre-pay penalty\nPayments to be $5,995.51 per month'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price includes (optional)
                    </label>
                    <textarea
                      name="price_includes"
                      rows={2}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      placeholder="Includes Paint Booth & 2 Air compressors"
                    />
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-6">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" name="is1031" defaultChecked={merged?.is1031 ?? false} className="rounded border-gray-300" />
                    1031 exchange
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" name="no_loan" defaultChecked className="rounded border-gray-300" />
                    No loan contingency
                  </label>
                </div>
                {/* ⭐ THE 101ST - HIS APPLEBEE'S CASE: an exchange buyer with no down-leg in escrow yet.
                    ⛔ A tick, never inferred from an empty date: on an ordinary 1031 a forgotten
                    date must still stop the letter, or "subject to the closing of the down-leg"
                    would vanish by accident. */}
                <label className="mt-2 flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" name="downleg_not_in_escrow" defaultChecked={merged?.downLegNotInEscrow ?? false} className="mt-0.5 rounded border-gray-300" />
                  <span>
                    1031, but the down-leg is not in escrow yet
                    <span className="block text-[11px] text-gray-400">
                      No close date to quote: the letter drops the escrow sentences and the down-leg
                      contingency, word for word as your Applebee&rsquo;s letter. Leave the date empty.
                    </span>
                  </span>
                </label>
                {/* ⭐ LOI-S5 (the 127th) - HIS 10-7 OLIVE GARDEN LETTER: the down-leg in escrow under an extension he
                    exercised, and the protection that goes with it. Each is OFF until he sets it; the builder refuses
                    half an extension and says which half is missing. */}
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    Down-leg extension (days)
                    <input
                      type="number"
                      name="downleg_ext_days"
                      min={1}
                      max={99}
                      step={1}
                      defaultValue={merged?.downLegExtensionDays ?? ''}
                      className="w-20 rounded border-gray-300 text-sm"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    commencing
                    <input
                      type="date"
                      name="downleg_ext_from"
                      aria-label="Down-leg extension commenced"
                      defaultValue={merged?.downLegExtensionFrom ?? ''}
                      className="rounded border-gray-300 text-sm"
                    />
                  </label>
                  <span className="text-xs text-gray-500">
                    Both or neither. The Buyer Profile and the 1031 section quote them, and the down-leg date above
                    is then the date it now closes by.
                  </span>
                </div>
                <label className="mt-2 flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" name="downleg_protection" defaultChecked={merged?.downLegProtection ?? false} className="mt-0.5 rounded border-gray-300" />
                  <span>
                    Down-leg protection
                    <span className="block text-[11px] text-gray-400">
                      The down-leg closing becomes a condition that survives the removal of contingencies. If it fails
                      by its date the Buyers may terminate and the whole deposit is refunded - the deposit paragraph
                      says so too. Word for word as your October 7 Olive Garden letter.
                    </span>
                  </span>
                </label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label htmlFor="loi_wording" className="block text-sm font-medium text-gray-700 mb-1">Letter wording</label>
                    <select id="loi_wording" name="wording" defaultValue={merged?.wording ?? 'september_2026'} className="w-full rounded-md border-gray-300 shadow-sm text-sm py-2">
                      <option value="september_2026">September 2026 - new offers</option>
                      <option value="june_2026">June 2026 - a counter to a letter sent before September</option>
                    </select>
                    <p className="text-[11px] text-gray-400 mt-1">
                      Every letter gets the same look. A counter keeps the words of the letter it answers.
                    </p>
                  </div>
                  <div>
                    <label htmlFor="loi_dd_list" className="block text-sm font-medium text-gray-700 mb-1">Due-diligence list</label>
                    <select id="loi_dd_list" name="dd_list" defaultValue={merged?.ddList ?? 'standard'} className="w-full rounded-md border-gray-300 shadow-sm text-sm py-2">
                      <option value="standard">Standard - 9 items</option>
                      {/* LOI-S5 (the 127th): the nine plus store sales and the REA "affecting the pad". ⚠️ Kept short: the closed
                          select clipped "(your Olive Garden list)" to "(your Olive G", and "(Olive Garden)" to
                          "(Olive Garder" - found by looking, twice. The line under the select says whose list it is. */}
                      <option value="restaurant_pad">Restaurant pad - 11 items</option>
                      <option value="single_tenant_restaurant">Single-tenant restaurant - 14 items (your Applebee&rsquo;s list)</option>
                      {/* AIW1-F1 (the 117th): no tenant items, and possession free of any tenancies. */}
                      <option value="vacant">Vacant / owner-user - 5 items (no tenant items)</option>
                    </select>
                    <p className="text-[11px] text-gray-400 mt-1">
                      The seller owes what this list names before due diligence starts. Restaurant pad is your
                      Olive Garden list: the nine plus store sales and the REA.
                    </p>
                  </div>
                </div>
                {/* ⭐ LOI-S5 (the 127th) - HIS 10-7 SECTIONS 5 AND 7. The clock: off, due diligence counts from mutual
                    execution as every earlier letter does. The closing: written around the down-leg extension, so the
                    builder refuses it without one and says so. */}
                <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" name="dd_from_deliverables" defaultChecked={merged?.ddFromDeliverables ?? false} className="mt-0.5 rounded border-gray-300" />
                  <span>
                    Count due diligence from my receipt of the Seller Deliverables
                    <span className="block text-[11px] text-gray-400">
                      Unticked, it counts from the date of mutual execution, as your letters before October 2026 do.
                    </span>
                  </span>
                </label>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    Closing waits (days) after due diligence
                    <input
                      type="number"
                      name="coe_after_dd"
                      min={1}
                      max={99}
                      step={1}
                      defaultValue={merged?.coeDaysAfterDd ?? ''}
                      className="w-20 rounded border-gray-300 text-sm"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    no later than
                    <input
                      type="date"
                      name="coe_outside"
                      aria-label="Closing outside date"
                      defaultValue={merged?.coeOutsideDate ?? ''}
                      className="rounded border-gray-300 text-sm"
                    />
                  </label>
                  <span className="text-xs text-gray-500">
                    Both or neither, and only with a down-leg extension - the paragraph describes it.
                  </span>
                </div>

                {/*
                  ⭐⭐ COUNTER-PROPOSAL. Measured across his letter corpus: an offer and a counter are
                  81 paragraphs each and 80 are IDENTICAL - the entire difference is the RE line. So
                  this is a tick, a number and a date, and they re-word that one line to his heading
                  (LOI-S2, the 125th): "Buyers' Counter Offer to Seller's Counter Offer No. 1 dated
                  September 10, 2026 - Letter of Intent". The number and the date each print only when given.
                  ⚠️ The control NAMES what it does - a bare "Counter" tick would not say which of the
                  seller's rounds it answers, and that number appears on the letter.
                */}

                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      name="is_counter"
                      defaultChecked={merged?.counterToSellerCounterNo != null}
                      className="rounded border-gray-300"
                    />
                    This answers a seller counter
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    Seller&rsquo;s counter no.
                    <input
                      type="number"
                      name="counter_no"
                      min={1}
                      step={1}
                      defaultValue={merged?.counterToSellerCounterNo ?? 1}
                      className="w-20 rounded border-gray-300 text-sm"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    dated
                    <input
                      type="date"
                      name="counter_date"
                      aria-label="Seller's counter dated"
                      className="rounded border-gray-300 text-sm"
                    />
                  </label>
                  <span className="text-xs text-gray-500">
                    Re-words the RE line only - the number and the date each print only when filled; every other
                    paragraph is unchanged.
                  </span>
                </div>
                {/* ⭐ LOI-S5 (the 127th) - A REVISED LETTER replaces HIS OWN earlier letter (his 9-16 and 10-7 Olive
                    Garden letters); a counter, above, answers the SELLER's. The date is the switch: empty is an
                    ordinary letter. ⛔ Never both - the builder refuses and says which to clear. */}
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    This revises my letter dated
                    <input
                      type="date"
                      name="revises_date"
                      defaultValue={merged?.revisesLetterDated ?? ''}
                      className="rounded border-gray-300 text-sm"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      name="revises_revised"
                      defaultChecked={merged?.revisesRevisedLetter ?? false}
                      className="rounded border-gray-300"
                    />
                    that letter was itself a revised letter
                  </label>
                  <span className="text-xs text-gray-500">
                    The RE line reads &ldquo;Revised Letter of Intent&rdquo; and the opening names the letter it
                    supersedes. A letter is a counter or a revision, never both.
                  </span>
                </div>
              </div>

              {!dealId && (
                <div data-file-letter className="rounded border border-gray-200 p-3 text-sm">
                  {/* ⛔ No `name`: filing is state, never a form field the letter could carry. */}
                  <label className="flex items-start gap-2 text-gray-800">
                    <input
                      type="checkbox"
                      checked={fileIt}
                      onChange={(e) => toggleFileIt(e.target.checked)}
                      className="mt-0.5 rounded border-gray-300"
                    />
                    <span>
                      File this letter in my property database
                      <span className="block text-[11px] text-gray-500">
                        The address is looked up first. If the building is already there the letter
                        files on that record; if it is close to one, you choose; if it is not there,
                        a record is created from the facts shown here.
                      </span>
                    </span>
                  </label>
                  {fileIt && (
                    <div className="mt-2 space-y-2 pl-6">
                      {planBusy && <p className="text-xs text-gray-600">Checking your property database…</p>}
                      {planError && <p className="text-xs text-red-700">{planError}</p>}
                      {!planBusy && planStale && (
                        <p className="text-xs text-amber-800">
                          The address changed since this was checked.{' '}
                          <button type="button" className="underline" onClick={() => { if (liveFacts) void checkFiling(liveFacts); }}>
                            Check again
                          </button>
                        </p>
                      )}
                      {!planBusy && filingPlan && !planStale && (
                        <div data-plan={filingPlan.decision} className="text-xs text-gray-700 space-y-1">
                          {filingPlan.decision === 'file_on_existing' && filingPlan.match && (
                            <p>
                              Files on <span className="font-semibold">{filingPlan.match.address}, {filingPlan.match.city}</span>
                              {' '}- already in your property database ({pct(filingPlan.match.confidence)} match).
                            </p>
                          )}
                          {filingPlan.decision === 'ask' && (
                            <div role="radiogroup" aria-label="Where to file this letter" className="space-y-1">
                              <p>{filingPlan.reason}</p>
                              {filingPlan.candidates.map((c) => (
                                <label key={c.propertyId} className="flex items-center gap-2">
                                  <input
                                    type="radio"
                                    checked={filingChoice === c.propertyId}
                                    onChange={() => setFilingChoice(c.propertyId)}
                                  />
                                  <span>
                                    <span className="font-semibold">{c.address}, {c.city}</span> - an existing record ({pct(c.confidence)} match)
                                  </span>
                                </label>
                              ))}
                              <label className="flex items-center gap-2">
                                <input type="radio" checked={filingChoice === 'new'} onChange={() => setFilingChoice('new')} />
                                <span>
                                  A new record for <span className="font-semibold">{liveFacts?.address}, {liveFacts?.city}</span>
                                </span>
                              </label>
                            </div>
                          )}
                          {filingPlan.decision === 'create' && <p>{filingPlan.reason}</p>}
                          {filingPlan.decision === 'refuse' && <p className="text-red-700">{filingPlan.reason}</p>}
                          {/* ⭐ The facts a new record would hold - LIVE from the form, because those
                              are exactly what will be written, not what was there when it was checked. */}
                          {(filingPlan.decision === 'create' || (filingPlan.decision === 'ask' && filingChoice === 'new')) && liveFacts && (
                            <dl data-would-create className="grid grid-cols-2 gap-x-3 rounded bg-gray-50 px-2 py-1 sm:grid-cols-3">
                              <div><dt className="text-gray-500">Address</dt><dd>{liveFacts.address}</dd></div>
                              <div><dt className="text-gray-500">City</dt><dd>{liveFacts.city}, {liveFacts.state}</dd></div>
                              <div><dt className="text-gray-500">ZIP</dt><dd>{liveFacts.zip ?? '-'}</dd></div>
                              <div><dt className="text-gray-500">Building SF</dt><dd>{liveFacts.buildingSf?.toLocaleString('en-US') ?? '-'}</dd></div>
                              <div><dt className="text-gray-500">Acres</dt><dd>{liveFacts.lotSizeAcres ?? '-'}</dd></div>
                            </dl>
                          )}
                        </div>
                      )}
                      {filingNotice && <p className="text-xs font-medium text-red-700">{filingNotice}</p>}
                    </div>
                  )}
                </div>
              )}

              {(ov.preview || ov.error || ov.busy) && (
                <LoiPreviewPanel
                  state={ov}
                  houseStyleSlot={(o) => <KeepAsHouseStyle override={o} readInput={readLetter} state={ov} />}
                />
              )}

              {/* ⛔ HIS ASKS NEVER VANISH IN SILENCE: while this letter cannot be built there is nothing to draft them
                    against, so they wait here, named - Generate now would file the letter without them. */}
              {!ov.preview && ov.error && ov.busy === null && chatAsks.length > 0 && (
                <p className="mt-3 rounded border border-violet-200 bg-violet-50 p-2 text-[12.5px] text-violet-900" data-testid="loi-chat-asks-waiting">
                  From the chat, waiting to be drafted: {chatAsks.map((a) => `"${a.words}"`).join('; ')}. Fill in what the
                  letter still needs, then press Preview the letter - each is drafted for you to accept.
                </p>
              )}

              {/* ⚠️ `flex-wrap`: three nowrap buttons need ~336px and a 375px phone leaves ~303px - Generate wraps below. */}
              <div className="flex flex-wrap gap-3 pt-4 border-t">
                {/* ⛔⛔ GENERATE WAITS WHILE A CHANGE OF HIS IS NOT YET IN THE LETTER (`generateWaits`) - filing now files
                      the letter without it. A control that waits says why. */}
                {generateWaits && <p className="w-full text-[12px] text-gray-500" data-testid="loi-generate-waits">{generateWaits}</p>}
                {/* ⭐ D6: his house style changes this letter whether or not he previews it - so it is said here, by count. */}
                {houseSections > 0 && (
                  <p className="w-full text-[12px] text-emerald-900" data-testid="loi-house-style-notice">
                    {houseSections === 1
                      ? 'Your house style changes 1 section of this letter - Preview the letter to see or skip it.'
                      : `Your house style changes ${houseSections} sections of this letter - Preview the letter to see or skip them.`}
                  </p>
                )}
                <Button type="button" variant="outline" onClick={close} className="flex-1">Cancel</Button>
                <Button type="button" variant="outline" onClick={() => void ov.refresh()} disabled={busy || ov.busy !== null}>Preview the letter</Button>
                <Button type="submit" isLoading={busy} disabled={busy || generateWaits !== null} className="flex-1">
                  {busy ? 'Generating...' : 'Generate LOI'}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
